// docs/116 CI7: the bard's corner. The six tunes, what is playing now (and who asked for it), and a
// coin for the hat to ask for one by name; everyone in the room hears it from the same moment.
import { useEffect, useState } from 'react';
import { Badge } from '../../../../components';
import { playSfx } from '../../../../../core/audio';
import { serverNow } from '../../../../../core/time';
import { TUNES } from '../tunes';
import { nextSlotInMs } from '../song';
import { useInn } from '../store';
import { SONG_TIP, type InnData } from '../types';
import type { VillageLook } from '../../types';
import { HeroPick, SidePanel, firstPayer, useOpenSound } from './common';

const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const KIND: Record<string, string> = { jig: 'a jig', reel: 'a reel', 'drinking song': 'a drinking song', 'slow air': 'a slow air', hornpipe: 'a hornpipe', carol: 'a winter carol' };

export function BardPanel({ data, look }: { data: InnData; look: VillageLook }): React.JSX.Element {
  useOpenSound('inn.coin_flip', 0.4);
  const playing = useInn((s) => s.playing);
  const [hero, setHero] = useState(() => firstPayer(data.payers, SONG_TIP)?.id);
  const [busy, setBusy] = useState<number>();
  const [said, setSaid] = useState<{ text: string; bad?: boolean }>();
  const [now, setNow] = useState(serverNow);
  useEffect(() => { const t = window.setInterval(() => setNow(serverNow()), 1000); return () => window.clearInterval(t); }, []);
  const payer = data.payers.find((p) => p.id === hero) ?? firstPayer(data.payers, SONG_TIP);
  const tune = playing ? TUNES[playing.tune] : undefined;
  const left = tune && playing ? Math.max(0, tune.seconds - (now - playing.startedAtMs) / 1000) : 0;
  const asked = !!playing?.by;

  const ask = async (n: number) => {
    if (!payer) return;
    setBusy(n);
    const ok = await data.act.song(n, payer.id);
    setBusy(undefined);
    if (ok) { playSfx('ui.coins', { gain: 0.4 }); setSaid({ text: `${payer.name} drops a coin in the hat. “${TUNES[n]?.name}”, coming up!` }); }
    else {
      playSfx('ui.error');
      setSaid({ bad: true, text: asked ? `The bard shakes their head: ${playing?.by}'s tune is still playing. Ask again when it ends.` : 'The bard can\'t play that one just now.' });
    }
  };
  return (
    <SidePanel name="bard" title="The bard" className="fk-innp__bardp">
      <div className="fk-innp__now" data-testid="inn-bard-now">
        {tune ? <>
          <span className="fk-innp__now-eyebrow">Playing now</span>
          <b className="fk-innp__now-name">♪ {tune.name}</b>
          <span className="fk-innp__now-sub">{asked ? `asked for by ${playing!.by}` : 'the bard\'s own choice'} · {mmss(left)} left</span>
          <span className="fk-innp__now-bar" aria-hidden="true"><i style={{ width: `${Math.min(100, (1 - left / tune.seconds) * 100)}%` }} /></span>
        </> : <>
          <span className="fk-innp__now-eyebrow">Between tunes</span>
          <span className="fk-innp__now-sub">The bard is tuning up and sipping their ale. They'll play again in about {Math.max(1, Math.round(nextSlotInMs(now) / 60_000))} {Math.round(nextSlotInMs(now) / 60_000) > 1 ? 'minutes' : 'minute'}, or toss a coin in the hat and name a tune.</span>
        </>}
      </div>
      <HeroPick payers={data.payers} value={payer?.id} onChange={setHero} label="Who tips" price={SONG_TIP} testid="inn-song-hero" />
      <ul className="fk-innp__tunes">
        {TUNES.map((t) => {
          const on = playing?.tune === t.id;
          return (
            <li key={t.id} className={`fk-innp__tune ${on ? 'is-playing' : ''}`}>
              <span className="fk-innp__tune-text">
                <b>{t.name}</b>
                <span className="fk-innp__tune-sub">{KIND[t.kind] ?? t.kind} · {mmss(t.seconds)}{t.season && <> <Badge tone={look.season === t.season ? 'blue' : 'grey'}>{t.season}</Badge></>}</span>
              </span>
              <button type="button" className="fk-innp__drink fk-innp__ask" disabled={busy !== undefined || !payer || payer.gold < SONG_TIP || on} data-testid={`inn-song-${t.id}`}
                title={`Tip ${SONG_TIP} gold and ask for “${t.name}”`} onClick={() => void ask(t.id)}>
                {on ? 'Playing' : <>Tip {SONG_TIP}g, ask</>}
              </button>
            </li>
          );
        })}
      </ul>
      {said && <p className={`fk-innp__said ${said.bad ? 'is-bad' : ''}`} role="status" data-testid="inn-song-said">{said.text}</p>}
    </SidePanel>
  );
}
